/**
 * RuleEvaluator
 * 
 * Evaluates events against alert rules with advanced functionality.
 * 
 * Features:
 * - Evaluates single rules and multiple rules with AND/OR logic
 * - Supports different data types (string, number, boolean)
 * - Handles server-specific rule filtering
 * - Implements efficient rule matching algorithms
 * - Supports process-specific targeting via name pattern matching
 * 
 * @module RuleEvaluator
 */

import { AlertType, AlertCondition, matchProcessNamePattern } from '../models/AlertModels';

/**
 * Logger for RuleEvaluator
 */
const Logger = {
  log: (...args) => console.log('[RuleEvaluator]', ...args),
  warn: (...args) => console.warn('[RuleEvaluator]', ...args),
  error: (...args) => console.error('[RuleEvaluator]', ...args),
  debug: (...args) => console.debug('[RuleEvaluator]', ...args)
};

/**
 * Rule evaluation logic types
 * @readonly
 * @enum {string}
 */
const RuleLogic = {
  AND: 'AND',
  OR: 'OR'
};

/**
 * Evaluator for alert rules
 * @class RuleEvaluator
 */
class RuleEvaluator {
  /**
   * Creates an instance of RuleEvaluator
   */
  constructor() {
    // Cache for previously evaluated rules to improve performance
    this.ruleCache = new Map();
    
    // Cache expiration time in milliseconds (5 minutes)
    this.cacheTTL = 5 * 60 * 1000;
    
    // Statistics
    this.stats = {
      rulesEvaluated: 0,
      cacheHits: 0,
      cacheMisses: 0,
      alertsTriggered: 0
    };
    
    Logger.log('RuleEvaluator created');
  }

  /**
   * Evaluates if an event satisfies an alert rule
   * 
   * @param {Object} rule - The rule to evaluate
   * @param {Object} event - The event to evaluate against the rule
   * @returns {boolean} True if the event satisfies the rule, false otherwise
   */
  evaluateRule(rule, event) {
    if (!rule || !event) {
      Logger.warn('Invalid rule or event provided for evaluation');
      return false;
    }
    
    // Add detailed logging for rule evaluation
    Logger.debug(`Evaluating rule: event=${rule.event}, condition=${rule.condition}, value=${rule.value}`);
    Logger.debug(`Against event data:`, event);
    
    // Handle process name targeting if enabled (for Process type events)
    if (rule.processNameTargetingEnabled && event.type === 'process') {
      const processName = event.name || event.process_name;
      
      if (!processName) {
        Logger.debug('Process name targeting enabled but event has no process name property');
        return false;
      }
      
      if (!matchProcessNamePattern(processName, rule.processNamePattern)) {
        Logger.debug(`Process name "${processName}" does not match pattern "${rule.processNamePattern}", rule failed`);
        return false;
      }
      
      Logger.debug(`Process name "${processName}" matches pattern "${rule.processNamePattern}", continuing evaluation`);
    } else if (rule.processNameTargetingEnabled && event.type !== 'process') {
      // Process name targeting enabled but not a process event
      Logger.debug('Process name targeting enabled but event is not a process event, ignoring pattern matching');
    }
    
    // Update statistics
    this.stats.rulesEvaluated++;
    
    // Generate a cache key for this rule and event
    const cacheKey = this._generateCacheKey(rule, event);
    
    // Check if we have a cached result
    const cachedResult = this._getCachedResult(cacheKey);
    if (cachedResult !== null) {
      this.stats.cacheHits++;
      Logger.debug(`Using cached result: ${cachedResult}`);
      return cachedResult;
    }
    
    this.stats.cacheMisses++;
    
    // Generic extraction of event value based on the rule's event property
    // 1. Try event[rule.event] first
    // 2. If not found, and event.value exists, use it if rule.event === 'value' or event.event === rule.event
    let eventValue = event[rule.event];
    if (
      eventValue === undefined &&
      event.value !== undefined &&
      (rule.event === 'value' || event.event === rule.event)
    ) {
      Logger.debug(`Event does not have property "${rule.event}", trying event.value`);
      eventValue = event.value;
    }
    
    // If we still don't have a value, the rule is not satisfied
    if (eventValue === undefined) {
      Logger.debug(`Event does not have property "${rule.event}" or a suitable alternative, rule failed`);
      Logger.debug(`Available event properties: ${Object.keys(event).join(', ')}`);
      this._cacheResult(cacheKey, false);
      return false;
    }
    
    Logger.debug(`Event property "${rule.event}" found with value: ${eventValue}`);
    
    // Evaluate the condition
    let result = false;
    
    switch (rule.condition) {
      case AlertCondition.EQUALS:
        result = this._evaluateEquals(eventValue, rule.value);
        break;
      case AlertCondition.NOT_EQUALS:
        result = !this._evaluateEquals(eventValue, rule.value);
        break;
      case AlertCondition.GREATER_THAN:
        result = this._evaluateGreaterThan(eventValue, rule.value);
        break;
      case AlertCondition.LESS_THAN:
        result = this._evaluateLessThan(eventValue, rule.value);
        break;
      case AlertCondition.GREATER_THAN_OR_EQUALS:
        result = this._evaluateGreaterThanOrEquals(eventValue, rule.value);
        break;
      case AlertCondition.LESS_THAN_OR_EQUALS:
        result = this._evaluateLessThanOrEquals(eventValue, rule.value);
        break;
      case AlertCondition.CONTAINS:
        result = this._evaluateContains(eventValue, rule.value);
        break;
      default:
        Logger.warn(`Unknown condition: ${rule.condition}`);
        result = false;
    }
    
    // Cache the result
    this._cacheResult(cacheKey, result);
    
    Logger.debug(`Rule evaluation result: ${result ? 'MATCHED' : 'NOT MATCHED'}`);
    return result;
  }
  
  /**
   * Evaluates if a value equals another value with type conversion
   * @private
   * @param {*} eventValue - The value from the event
   * @param {*} ruleValue - The value from the rule
   * @returns {boolean} True if the values are equal
   */
  _evaluateEquals(eventValue, ruleValue) {
    // For numbers, convert string representations
    if (typeof eventValue === 'number' || typeof ruleValue === 'number') {
      return Number(eventValue) === Number(ruleValue);
    }
    
    // For booleans, handle string representations
    if (typeof eventValue === 'boolean' || typeof ruleValue === 'boolean') {
      // Convert string 'true'/'false' to boolean if needed
      const evVal = typeof eventValue === 'string' ? eventValue.toLowerCase() === 'true' : Boolean(eventValue);
      const ruVal = typeof ruleValue === 'string' ? ruleValue.toLowerCase() === 'true' : Boolean(ruleValue);
      return evVal === ruVal;
    }
    
    // For strings, case-insensitive comparison
    if (typeof eventValue === 'string' && typeof ruleValue === 'string') {
      return eventValue.toLowerCase() === ruleValue.toLowerCase();
    }
    
    // Default strict equality
    return eventValue === ruleValue;
  }
  
  /**
   * Evaluates if a value is greater than another value
   * @private
   * @param {*} eventValue - The value from the event
   * @param {*} ruleValue - The value from the rule
   * @returns {boolean} True if eventValue is greater than ruleValue
   */
  _evaluateGreaterThan(eventValue, ruleValue) {
    // Only applies to numbers
    if (typeof eventValue !== 'number' && typeof eventValue !== 'string') {
      return false;
    }
    
    return Number(eventValue) > Number(ruleValue);
  }
  
  /**
   * Evaluates if a value is less than another value
   * @private
   * @param {*} eventValue - The value from the event
   * @param {*} ruleValue - The value from the rule
   * @returns {boolean} True if eventValue is less than ruleValue
   */
  _evaluateLessThan(eventValue, ruleValue) {
    // Only applies to numbers
    if (typeof eventValue !== 'number' && typeof eventValue !== 'string') {
      return false;
    }
    
    return Number(eventValue) < Number(ruleValue);
  }
  
  /**
   * Evaluates if a value is greater than or equal to another value
   * @private
   * @param {*} eventValue - The value from the event
   * @param {*} ruleValue - The value from the rule
   * @returns {boolean} True if eventValue is greater than or equal to ruleValue
   */
  _evaluateGreaterThanOrEquals(eventValue, ruleValue) {
    // Only applies to numbers
    if (typeof eventValue !== 'number' && typeof eventValue !== 'string') {
      return false;
    }
    
    return Number(eventValue) >= Number(ruleValue);
  }
  
  /**
   * Evaluates if a value is less than or equal to another value
   * @private
   * @param {*} eventValue - The value from the event
   * @param {*} ruleValue - The value from the rule
   * @returns {boolean} True if eventValue is less than or equal to ruleValue
   */
  _evaluateLessThanOrEquals(eventValue, ruleValue) {
    // Only applies to numbers
    if (typeof eventValue !== 'number' && typeof eventValue !== 'string') {
      return false;
    }
    
    return Number(eventValue) <= Number(ruleValue);
  }
  
  /**
   * Evaluates if a value contains another value
   * @private
   * @param {*} eventValue - The value from the event
   * @param {*} ruleValue - The value from the rule
   * @returns {boolean} True if eventValue contains ruleValue
   */
  _evaluateContains(eventValue, ruleValue) {
    // Only applies to strings and arrays
    if (typeof eventValue === 'string') {
      return eventValue.toLowerCase().includes(String(ruleValue).toLowerCase());
    }
    
    if (Array.isArray(eventValue)) {
      return eventValue.some(item => this._evaluateEquals(item, ruleValue));
    }
    
    return false;
  }
  
  /**
   * Evaluates multiple rules against an event with AND/OR logic
   * 
   * @param {Object[]} rules - Array of rules to evaluate
   * @param {Object} event - The event to evaluate against the rules
   * @param {string} [logic=RuleLogic.AND] - Logic to use for combining rule results
   * @returns {boolean} True if the event satisfies the rules based on the logic
   */
  evaluateRules(rules, event, logic = RuleLogic.AND) {
    if (!Array.isArray(rules) || rules.length === 0) {
      Logger.warn('Invalid or empty rules array provided for evaluation');
      return false;
    }
    
    if (!event) {
      Logger.warn('Invalid event provided for rules evaluation');
      return false;
    }
    
    // Evaluate each rule
    const results = rules.map(rule => this.evaluateRule(rule, event));
    
    // Apply the logic
    if (logic === RuleLogic.AND) {
      // All rules must be satisfied
      return results.every(result => result === true);
    } else if (logic === RuleLogic.OR) {
      // At least one rule must be satisfied
      return results.some(result => result === true);
    }
    
    // Default to AND logic if invalid logic is provided
    Logger.warn(`Invalid logic provided: ${logic}, defaulting to AND`);
    return results.every(result => result === true);
  }
  
  /**
   * Evaluates an alert against an event
   * 
   * @param {Object} alert - The alert to evaluate
   * @param {Object} event - The event to evaluate against the alert
   * @param {string} [serverId] - Optional server ID for server-specific filtering
   * @returns {boolean} True if the alert is triggered by the event
   */
  evaluateAlert(alert, event, serverId = null) {
    // If the alert is disabled, it's not triggered
    if (!alert.enabled) {
      return false;
    }
    
    // Check server scope if serverId is provided
    if (serverId && alert.scope.type === 'server-specific') {
      // If alert is server-specific, check if it applies to this server
      if (!alert.scope.serverIds || !alert.scope.serverIds.includes(serverId)) {
        return false;
      }
    }
    
    // Evaluate all rules in the alert (using AND logic by default)
    return this.evaluateRules(alert.rules, event);
  }
  
  /**
   * Generates a cache key for a rule and event
   * @private
   * @param {Object} rule - The rule
   * @param {Object} event - The event
   * @returns {string} Cache key
   */
  _generateCacheKey(rule, event) {
    // Use the most unique properties of both rule and event to create a cache key
    const eventKey = event.id || event.timestamp || JSON.stringify(event);
    
    // Include process name targeting fields in the cache key if they exist
    const processTargetingKey = rule.processNameTargetingEnabled ? 
      `_processTarget_${rule.processNamePattern}` : '';
    
    return `${rule.event}_${rule.condition}_${rule.value}${processTargetingKey}_${eventKey}`;
  }
  
  /**
   * Gets a cached result if available and not expired
   * @private
   * @param {string} key - Cache key
   * @returns {boolean|null} Cached result or null if not found
   */
  _getCachedResult(key) {
    if (!this.ruleCache.has(key)) {
      return null;
    }
    
    const cached = this.ruleCache.get(key);
    const now = Date.now();
    
    // Check if the cached result has expired
    if (now - cached.timestamp > this.cacheTTL) {
      this.ruleCache.delete(key);
      return null;
    }
    
    return cached.result;
  }
  
  /**
   * Caches a rule evaluation result
   * @private
   * @param {string} key - Cache key
   * @param {boolean} result - Evaluation result
   */
  _cacheResult(key, result) {
    // Ensure cache doesn't grow too large
    if (this.ruleCache.size >= 1000) {
      // Remove oldest entries
      const keys = Array.from(this.ruleCache.keys());
      const oldestKeys = keys.sort((a, b) => 
        this.ruleCache.get(a).timestamp - this.ruleCache.get(b).timestamp
      ).slice(0, 100); // Remove oldest 100 entries
      
      oldestKeys.forEach(k => this.ruleCache.delete(k));
    }
    
    this.ruleCache.set(key, {
      result,
      timestamp: Date.now()
    });
  }
  
  /**
   * Processes a batch of events against a set of alerts
   * 
   * @param {Object[]} alerts - Array of alerts to evaluate
   * @param {Object[]} events - Array of events to evaluate
   * @param {string} [serverId] - Optional server ID for server-specific filtering
   * @returns {Object} Object with triggeredAlerts mapping alertId to array of events that triggered it
   */
  processBatch(alerts, events, serverId = null) {
    if (!Array.isArray(alerts) || !Array.isArray(events)) {
      Logger.warn('Invalid alerts or events array provided for batch processing');
      return { triggeredAlerts: {} };
    }
    
    const triggeredAlerts = {};
    
    // Process each alert against each event
    for (const alert of alerts) {
      if (!alert.enabled) continue;
      
      // Skip server-specific alerts that don't match the provided serverId
      if (serverId && alert.scope.type === 'server-specific') {
        if (!alert.scope.serverIds || !alert.scope.serverIds.includes(serverId)) {
          continue;
        }
      }
      
      // Log process name targeting info for Process type alerts
      if (alert.type === 'Process') {
        const hasProcessTargeting = alert.rules.some(rule => rule.processNameTargetingEnabled);
        if (hasProcessTargeting) {
          const processEvents = events.filter(event => event.type === 'process');
          Logger.debug(`Processing alert ${alert.id} with process name targeting against ${processEvents.length} process events`);
        }
      }
      
      for (const event of events) {
        if (this.evaluateRules(alert.rules, event)) {
          // Initialize array for this alert if not exists
          if (!triggeredAlerts[alert.id]) {
            triggeredAlerts[alert.id] = [];
          }
          
          // Add event to triggered events for this alert
          triggeredAlerts[alert.id].push(event);
          
          // Update statistics
          this.stats.alertsTriggered++;
        }
      }
    }
    
    return { triggeredAlerts };
  }
  
  /**
   * Gets the current statistics for the rule evaluator
   * 
   * @returns {Object} Statistics object
   */
  getStatistics() {
    return { ...this.stats };
  }
  
  /**
   * Resets the statistics counters
   */
  resetStatistics() {
    this.stats = {
      rulesEvaluated: 0,
      cacheHits: 0,
      cacheMisses: 0,
      alertsTriggered: 0
    };
  }
  
  /**
   * Clears the rule evaluation cache
   */
  clearCache() {
    this.ruleCache.clear();
    Logger.log('Rule evaluation cache cleared');
  }
  
  /**
   * Gets detailed information about a rule evaluation for debugging
   * 
   * @param {Object} rule - The rule to evaluate
   * @param {Object} event - The event to evaluate against the rule
   * @returns {Object} Detailed evaluation information
   */
  getDebugInfo(rule, event) {
    if (!rule || !event) {
      return { error: 'Invalid rule or event provided' };
    }
    
    const eventValue = event[rule.event];
    const result = this.evaluateRule(rule, event);
    
    // Prepare debug information specific to process name targeting
    const processTargetingInfo = {};
    if (rule.processNameTargetingEnabled) {
      const processName = event.type === 'process' ? (event.name || event.process_name) : null;
      processTargetingInfo.processNameTargetingEnabled = true;
      processTargetingInfo.processNamePattern = rule.processNamePattern;
      processTargetingInfo.processName = processName;
      processTargetingInfo.isProcessEvent = event.type === 'process';
      processTargetingInfo.patternMatched = processName ? matchProcessNamePattern(processName, rule.processNamePattern) : false;
    }
    
    return {
      rule,
      event,
      eventValue,
      result,
      details: {
        eventValueType: typeof eventValue,
        ruleValueType: typeof rule.value,
        condition: rule.condition,
        cacheKey: this._generateCacheKey(rule, event),
        processTargeting: rule.processNameTargetingEnabled ? processTargetingInfo : null
      }
    };
  }
}

export default RuleEvaluator;
export { RuleLogic };
